import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Spinner,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Tooltip,
  useColorModeValue,
  SkeletonText
} from '@chakra-ui/react';

import countries from 'i18n-iso-countries';
import { useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt } from "react-icons/fa";
import { useGetGeoLocationsQuery, useGetLocationMetaMutation } from 'store/facebookServices/facebookServicesApi';
import { formatNumber } from 'utils/functions';

countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

const LocationSelectorForBidMultiplier = ({ onLocationsChange, adsAccountId, value }) => {
  const inputBackground = useColorModeValue("#f8f6fb", "#1e1530");
  const borderColor = useColorModeValue('gray.300', 'gray.500');
  const hoverColor = useColorModeValue('#f0f0f0', 'gray.700');

  const inputRef = useRef(null);
  const popupRef = useRef(null);
  const hasInitializedRef = useRef(false);

  const [selectedLocations, setSelectedLocations] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [locationOptions, setLocationOptions] = useState([]);
  const [showPopup, setShowPopup] = useState(false);

  const { data: locations, isLoading, isFetching, refetch } = useGetGeoLocationsQuery(
    { searchTerm: debouncedSearchTerm, adsAccountId },
    {}
  );

  const [getLocationMeta] = useGetLocationMetaMutation();

  useEffect(() => {
    if (hasInitializedRef.current) return;
    
    const enrichLocations = async () => {
      if (!value?.locationFull?.length) return;
  
      const missingNameLocs = value.locationFull.filter(loc => !loc.name);
      const locsWithName = value.locationFull.filter(loc => loc.name);
  
      if (missingNameLocs.length === 0) {
        const enriched = locsWithName.map(loc => ({
          ...loc,
          multiplier: parseFloat(loc.multiplier ?? 1.0),
        }));
        setSelectedLocations(enriched);
        hasInitializedRef.current = true;
        return;
      }
  
      const payload = {
        type: "adgeolocationmeta",
        countries: [],
        regions: [],
        cities: [],
      };
  
      missingNameLocs.forEach(loc => {
        if (loc.type === 'country') {
          payload.countries.push(loc.key);
        } else if (loc.type === 'region') {
          payload.regions.push(loc.key);
        } else if (loc.type === 'city') {
          payload.cities.push(loc.key);
        }
      });
  
      try {
        const res = await getLocationMeta({ payload }).unwrap();
  
        const enrichedResults = [
          ...Object.values(res.countries ?? {}),
          ...Object.values(res.regions ?? {}),
          ...Object.values(res.cities ?? {})
        ];
  
        const combined = [...locsWithName, ...missingNameLocs.map(loc => {
          const found = enrichedResults.find(enriched => enriched.key === loc.key);
          return {
            ...loc,
            ...found,
            multiplier: parseFloat(loc.multiplier ?? 1.0),
          };
        })];
  
        setSelectedLocations(combined);
      } catch (error) {
        console.error("Error fetching location meta", error);
        const fallback = value.locationFull.map(loc => ({
          ...loc,
          multiplier: parseFloat(loc.multiplier ?? 1.0),
        }));
        setSelectedLocations(fallback);
      } finally {
        hasInitializedRef.current = true;
      }
    };
  
    enrichLocations();
  }, [value?.locationFull]);

  const shouldRemoveLocation = (newLoc, existingLoc) => {
      const isSameCountry = newLoc.country_code === existingLoc.country_code;

      const isSameLocation =
          newLoc.key === existingLoc.key &&
          newLoc.type === existingLoc.type;

      const isCountryRemovedByZip =
          newLoc.type === "zip" &&
          existingLoc.type === "country" &&
          isSameCountry;

      const isZipRemovedByCountry =
          newLoc.type === "country" &&
          existingLoc.type === "zip" &&
          isSameCountry;

      const isRegionOrCityReplacedByCountry =
          newLoc.type === "country" &&
          (existingLoc.type === "region" || existingLoc.type === "city") &&
          isSameCountry;

      const isCountryReplacedByRegionOrCity =
          (newLoc.type === "region" || newLoc.type === "city") &&
          existingLoc.type === "country" &&
          isSameCountry;

      return (
          isCountryRemovedByZip ||
          isZipRemovedByCountry ||
          isRegionOrCityReplacedByCountry ||
          isCountryReplacedByRegionOrCity ||
          isSameLocation
      );
  };

  const filterSelectedLocations = (newLoc, existing) => {
    const updated = existing.filter(loc => !shouldRemoveLocation(newLoc, loc));
    return [...updated, newLoc];
  };

  const updateMultiplier = (index, value) => {
    setSelectedLocations(prev => {
      const updated = [...prev];
      updated[index].multiplier = value;
      return updated;
    });
  };

  const handleLocationSelect = (location) => {
    const locWithExtras = {
      ...location,
      multiplier: 1.0,
    };

    setSelectedLocations(prev => {
      const updated = filterSelectedLocations(locWithExtras, prev);
      // Remove duplicates by key and type
      const unique = updated.filter(
        (v, i, self) => i === self.findIndex(t => t.key === v.key)
      );
      return unique;
    });

    setSearchTerm('');
    setDebouncedSearchTerm('');
    setShowPopup(false);
    setLocationOptions([]);
  };

  const removeLocation = (id) => {
    setSelectedLocations(prev => prev.filter(loc => loc.key !== id));
  };

  useEffect(() => {
    if (searchTerm.length === 0) {
      setDebouncedSearchTerm('');
      setShowPopup(false);
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (searchTerm === debouncedSearchTerm) {
      refetch();
    }
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    if (locations?.data?.length > 0) {
      setShowPopup(true);
      setLocationOptions(locations.data);
    } else {
      setShowPopup(false);
    }
  }, [locations]);

  useEffect(() => {
    const handler = (event) => {
      if (
        inputRef.current && !inputRef.current.contains(event.target) &&
        popupRef.current && !popupRef.current.contains(event.target)
      ) {
        setShowPopup(false);
        setLocationOptions([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const payload = {};
  
    const cities = {};
    const regions = {};
    const countries = {};
  
    selectedLocations.forEach(loc => {
      const multiplier = parseFloat(loc.multiplier ?? 1.0);
      if (loc.type === 'city') {
        cities[loc.key] = multiplier;
      } else if (loc.type === 'region') {
        regions[loc.key] = multiplier;
      } else if (loc.type === 'country') {
        countries[loc.key] = multiplier;
      }
    });
  
    if (Object.keys(cities).length > 0) payload.cities = cities;
    if (Object.keys(regions).length > 0) payload.regions = regions;
    if (Object.keys(countries).length > 0) payload.countries = countries;
  
    onLocationsChange({ payload, fullSelectedLocations: selectedLocations });
  }, [selectedLocations]);  

  return (
    <Box>
      {/* Location search input */}
      {isLoading ? (
        <Flex width="100%" mt="10px">
            <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
        </Flex>
        ) : (
        <Box position="relative" mt="10px">
          <InputGroup>
            <InputLeftElement height="32px">
              <SearchIcon boxSize={4} color="gray.500" />
            </InputLeftElement>
            <Input
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search locations"
              size="sm"
              borderRadius="md"
              onFocus={() => {
                setLocationOptions([]);
                if (locationOptions.length > 0) setShowPopup(true);
              }}
            />
            <InputRightElement height="32px">
              {isFetching ? (
                <Spinner boxSize={3} />
              ) : (
                searchTerm.length > 0 && (
                  <CloseIcon boxSize={3} color="gray.500" onClick={() => setSearchTerm('')} />
                )
              )}
            </InputRightElement>
          </InputGroup>

          {showPopup && locationOptions.length > 0 && (
              <List
                mt={2}
                ref={popupRef}
                top="100%"
                width="100%"
                border="1px solid"
                borderColor={borderColor}
                backgroundColor={inputBackground}
                zIndex="1000"
                maxHeight="200px"
                overflowY="auto"
              >
                  {locationOptions
                      .filter(loc => ['city', 'region', 'country'].includes(loc.type))
                      .map((loc) => (
                          <ListItem
                          key={loc.region_id + loc.name}
                          p="5px"
                          px="10px"
                          cursor="pointer"
                          _hover={{ backgroundColor: hoverColor }}
                          onClick={() => handleLocationSelect(loc)}
                          >
                          <Flex justify="space-between">
                              <Text fontSize="14px">{loc.name}</Text>
                              <Text fontSize="12px">{loc.type}</Text>
                          </Flex>
                          </ListItem>
                      ))
                  }
              </List>
            )
          }
        </Box>
      )}

      {/* Selected locations with slider */}
      <Box mt="10px" maxHeight="300px" overflowY="auto">
        {selectedLocations?.map((location, index) => (
          <Box key={`${location.key}-${index}`} mb={4} bg={inputBackground} p={3} borderRadius="md">
            <Flex align="center" justify="space-between">
              <Flex align="center">
                <FaMapMarkerAlt
                  color="#00A500"
                  size="16px"
                  style={{ marginRight: 6 }}
                />
                <Text fontSize="12px" fontWeight="bold">{location.name}</Text>
              </Flex>
              <CloseIcon
                _hover={{ cursor: 'pointer', color: 'red.400' }}
                boxSize={3}
                color="gray.500"
                onClick={() => removeLocation(location.key)}
              />
            </Flex>

            {(value?.placement || value?.os) ? null : (
              <Flex mt={3} align="center" gap={4}>
                <Flex flex={'95%'} align="center">
                  <Slider
                    min={0.09}
                    max={1.0}
                    step={0.01}
                    value={location.multiplier ?? 1.0}
                    onChange={(val) => updateMultiplier(index, val)}
                    colorScheme="brand"
                    flex="1"
                  >
                    <SliderTrack>
                      <SliderFilledTrack />
                    </SliderTrack>
                    <Tooltip
                      hasArrow
                      placement="top"
                      label={(formatNumber(location.multiplier ?? 1.0))}
                      bg="gray.700"
                      color="white"
                      fontSize="xs"
                    >
                      <SliderThumb />
                    </Tooltip>
                  </Slider>
                </Flex>
              
                <Flex flex={'5%'} justify="center">
                  <Text fontSize="12px">{(formatNumber(location.multiplier ?? 1.0))}</Text>
                </Flex>
              </Flex>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default LocationSelectorForBidMultiplier;
